import React, { useEffect, useState } from "react"
import { Icon } from "~/react/ui/icon"
import { Skeleton } from "~/react/ui/skeleton"
import { Count } from "~/react/ui/count"
import { Card, CardTitle, CardTitleActions, CardContent } from "~/react/ui/card"
import type { CampaignIssue } from "~/types/serializers"

interface CampaignsIssuesProps {
  endpoint: string
  refreshInterval?: number
}

const IssueItem: React.FC<{ issue: CampaignIssue }> = ({ issue }) => {
  const isCritical = issue.severity === "critical"
  const severityLabel = isCritical
    ? i18n.t("js.campaigns.issues.critical")
    : i18n.t("js.campaigns.issues.medium")

  // Convert icon_name from "fa-exclamation-circle" to "exclamation-circle"
  const iconName = issue.icon_name.replace("fa-", "")

  return (
    <li className={`issues-feed-item ${issue.css_class}`}>
      <h3 className="text-xs-medium m-0">{issue.title}</h3>
      <p className="text-2xs text-hint m-0">{issue.description}</p>
      <footer className="issues-feed-item__footer">
        <a
          href={issue.action_url}
          className="h-button h-button--xs"
          {...issue.action_html_options}
        >
          {issue.action_text}
        </a>
        <div className="issues-feed-item__type">
          <Icon
            icon={iconName}
            size="xs"
            className="issues-feed-item__icon"
          />
          <span className="text-2xs-medium">{severityLabel}</span>
        </div>
      </footer>
    </li>
  )
}

const EmptyState: React.FC = () => (
  <div className="empty-state py-4">
    <Icon
      icon="envelope-circle-check"
      size="xl"
      className="empty-state__icon"
    />
    <div className="empty-state__content">
      <h3 className="empty-state__title text-sm-medium">
        {i18n.t("js.campaigns.issues.empty_state.title")}
      </h3>
      <p className="empty-state__description text-2xs">
        {i18n.t("js.campaigns.issues.empty_state.description")}
      </p>
    </div>
  </div>
)

const LoadingSkeleton: React.FC = () => (
  <>
    <div className="issues-feed-item mt-2">
      <Skeleton
        size="freeform"
        style={{ width: "75%", height: "16px", borderRadius: "4px" }}
        className="mb-2"
      />
      <Skeleton
        size="freeform"
        style={{ width: "100%", height: "12px", borderRadius: "4px" }}
      />
    </div>
    <div className="issues-feed-item">
      <Skeleton
        size="freeform"
        style={{ width: "75%", height: "16px", borderRadius: "4px" }}
        className="mb-2"
      />
      <Skeleton
        size="freeform"
        style={{ width: "100%", height: "12px", borderRadius: "4px" }}
      />
    </div>
    <div className="issues-feed-item">
      <Skeleton
        size="freeform"
        style={{ width: "75%", height: "16px", borderRadius: "4px" }}
        className="mb-2"
      />
      <Skeleton
        size="freeform"
        style={{ width: "100%", height: "12px", borderRadius: "4px" }}
      />
    </div>
  </>
)

const CampaignsIssues: React.FC<CampaignsIssuesProps> = ({
  endpoint,
  refreshInterval,
}) => {
  const [issues, setIssues] = useState<CampaignIssue[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchIssues = () => {
      fetch(endpoint, {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error ${res.status}`)
          return res.json()
        })
        .then(setIssues)
        .catch((error) => {
          console.error("Failed to fetch campaign issues:", error)
        })
        .finally(() => setLoading(false))
    }

    fetchIssues()

    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(fetchIssues, refreshInterval)
      return () => clearInterval(interval)
    }
  }, [endpoint, refreshInterval])

  return (
    <Card variant="widget" className="pb-0">
      <header className="widget-card__header">
        <CardTitle>
          {i18n.t("js.campaigns.issues.title")}
          {!loading && issues.length > 0 ? <Count number={issues.length.toString()} className="ml-2" /> : ""}
          <CardTitleActions>
            <button
              className="question-circle"
              data-controller="tooltip"
              title={i18n.t("js.campaigns.issues.tooltip")}
            >
              <Icon icon="question-circle" size="sm" />
            </button>
          </CardTitleActions>
        </CardTitle>
      </header>
      <CardContent>
        {loading ? (
          <LoadingSkeleton />
        ) : issues.length > 0 ? (
          <ul className="issues-feed">
            {issues.map((issue) => (
              <IssueItem key={issue.id} issue={issue} />
            ))}
          </ul>
        ) : (
          <EmptyState />
        )}
      </CardContent>
    </Card>
  )
}

export default CampaignsIssues
